import React from 'react';
import { BookOpen, TrendingUp, AlertCircle } from 'lucide-react';
import { SecondaryModuleProps } from '../../types/secondaryModule';
import { SecondaryModuleContainer } from './SecondaryModuleContainer';
export function KnowledgeModule({
  moduleId,
  isOpen,
  onClose
}: SecondaryModuleProps) {
  const data = {
    learningStatus: {
      subjectHealthIndex: [{
        subject: '语文',
        score: 85
      }, {
        subject: '数学',
        score: 78
      }, {
        subject: '英语',
        score: 82
      }, {
        subject: '物理',
        score: 75
      }, {
        subject: '化学',
        score: 88
      }],
      assignmentQuality: {
        correctRate: 96,
        excellentRate: 91,
        goodRate: 95
      },
      knowledgePointMastery: [{
        point: '函数与方程',
        masteryRate: 85
      }, {
        point: '立体几何',
        masteryRate: 72
      }, {
        point: '概率统计',
        masteryRate: 78
      }]
    },
    teachingEffectiveness: {
      teacherGradingEfficiency: 92,
      commonMistakes: [{
        topic: '二次函数应用',
        errorRate: 28
      }, {
        topic: '立体几何证明',
        errorRate: 35
      }, {
        topic: '概率计算',
        errorRate: 22
      }]
    },
    aiInsights: [{
      type: 'finding' as const,
      content: '本周作业正确率达96%，创本学期新高'
    }, {
      type: 'finding' as const,
      content: '立体几何知识点掌握率偏低，仅为72%'
    }, {
      type: 'suggestion' as const,
      content: '建议针对立体几何开展专项辅导，强化空间想象能力'
    }, {
      type: 'suggestion' as const,
      content: '建议增加二次函数应用题的练习量，提升实际应用能力'
    }, {
      type: 'prediction' as const,
      content: '预计经过两周专项训练，立体几何掌握率可提升至85%以上'
    }]
  };
  return <SecondaryModuleContainer moduleId={moduleId} isOpen={isOpen} onClose={onClose} data-id="element-280">
      <div className="grid grid-cols-3 gap-6" data-id="element-281">
        <div className="col-span-2 space-y-6" data-id="element-282">
          {/* Assignment Quality */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-283">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-284">
              <div className="w-1 h-5 bg-[#38BDF8] rounded-full" data-id="element-285"></div>
              学情整体态势
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-286">
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-287">
                <div className="text-3xl font-bold text-[#38BDF8] mb-1" data-id="element-288">
                  {data.learningStatus.assignmentQuality.correctRate}%
                </div>
                <div className="text-sm text-gray-400" data-id="element-289">作业正确率</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-290">
                <div className="text-3xl font-bold text-green-400 mb-1" data-id="element-291">
                  {data.learningStatus.assignmentQuality.excellentRate}%
                </div>
                <div className="text-sm text-gray-400" data-id="element-292">优秀率</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-293">
                <div className="text-3xl font-bold text-blue-400 mb-1" data-id="element-294">
                  {data.learningStatus.assignmentQuality.goodRate}%
                </div>
                <div className="text-sm text-gray-400" data-id="element-295">良好率</div>
              </div>
            </div>
          </div>

          {/* Subject Health Index */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-296">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-297">
              <BookOpen className="w-5 h-5 text-[#38BDF8]" data-id="element-298" />
              学科健康度指数
            </h3>
            <div className="space-y-4" data-id="element-299">
              {data.learningStatus.subjectHealthIndex.map((subject, index) => <div key={index} data-id="element-300">
                  <div className="flex items-center justify-between mb-2" data-id="element-301">
                    <span className="text-white text-sm" data-id="element-302">
                      {subject.subject}
                    </span>
                    <span className="text-[#38BDF8] font-bold" data-id="element-303">
                      {subject.score}
                    </span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden" data-id="element-304">
                    <div className="h-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] rounded-full" style={{
                  width: `${subject.score}%`
                }} data-id="element-305"></div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Knowledge Point Mastery */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-306">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-307">
              <TrendingUp className="w-5 h-5 text-[#38BDF8]" data-id="element-308" />
              知识点掌握情况
            </h3>
            <div className="space-y-3" data-id="element-309">
              {data.learningStatus.knowledgePointMastery.map((point, index) => <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-id="element-310">
                  <span className="text-white" data-id="element-311">{point.point}</span>
                  <div className="flex items-center gap-3" data-id="element-312">
                    <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-313">
                      <div className={`h-full rounded-full ${point.masteryRate >= 80 ? 'bg-green-400' : point.masteryRate >= 70 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{
                    width: `${point.masteryRate}%`
                  }} data-id="element-314"></div>
                    </div>
                    <span className="text-white font-bold w-12 text-right" data-id="element-315">
                      {point.masteryRate}%
                    </span>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-316">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-317">
              <AlertCircle className="w-5 h-5 text-orange-400" data-id="element-318" />
              错题共性分析
            </h3>
            <div className="space-y-3" data-id="element-319">
              {data.teachingEffectiveness.commonMistakes.map((mistake, index) => <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-id="element-320">
                    <span className="text-white" data-id="element-321">{mistake.topic}</span>
                    <div className="flex items-center gap-3" data-id="element-322">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-323">
                        <div className="h-full bg-gradient-to-r from-red-400 to-orange-400 rounded-full" style={{
                    width: `${mistake.errorRate}%`
                  }} data-id="element-324"></div>
                      </div>
                      <span className="text-red-400 font-bold w-12 text-right" data-id="element-325">
                        {mistake.errorRate}%
                      </span>
                    </div>
                  </div>)}
            </div>
          </div>
        </div>

        {/* Right Column: AI Insights */}
        <div className="space-y-6" data-id="element-326">
          <div className="glass-panel p-6 rounded-xl border border-[#0EA5E9]/30 bg-gradient-to-br from-[#0EA5E9]/10 to-transparent" data-id="element-327">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-328">
              <div className="w-8 h-8 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center" data-id="element-329">
                <BookOpen className="w-5 h-5 text-[#0EA5E9]" data-id="element-330" />
              </div>
              易教智能体洞察
            </h3>
            <p className="text-sm text-gray-400 mb-6" data-id="element-331">
              基于学情数据分析，易教智能体为您生成以下洞察：
            </p>

            <div className="space-y-4" data-id="element-332">
              <div data-id="element-333">
                <div className="text-sm font-bold text-[#38BDF8] mb-2" data-id="element-334">
                  💡 关键发现
                </div>
                <div className="space-y-2" data-id="element-335">
                  {data.aiInsights.filter(insight => insight.type === 'finding').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-[#38BDF8]/30 py-1" data-id="element-336">
                        {insight.content}
                      </div>)}
                </div>
              </div>

              <div data-id="element-337">
                <div className="text-sm font-bold text-green-400 mb-2" data-id="element-338">
                  🎯 建议措施
                </div>
                <div className="space-y-2" data-id="element-339">
                  {data.aiInsights.filter(insight => insight.type === 'suggestion').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-green-400/30 py-1" data-id="element-340">
                        {insight.content}
                      </div>)}
                </div>
              </div>

              <div data-id="element-341">
                <div className="text-sm font-bold text-orange-400 mb-2" data-id="element-342">
                  📊 预测趋势
                </div>
                <div className="space-y-2" data-id="element-343">
                  {data.aiInsights.filter(insight => insight.type === 'prediction').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-orange-400/30 py-1" data-id="element-344">
                        {insight.content}
                      </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SecondaryModuleContainer>;
}